import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INDUSTRIES } from "@/data/industries";
import { PROJECT_TYPES } from "@/data/site";
import { api, errMsg } from "@/lib/api";
import { SectionHead } from "./Sections";

const TAG_BANDS = ["Under 500", "500 to 2,000", "2,000 to 10,000", "10,000 to 50,000", "Over 50,000"];
const EMPTY = { name: "", company: "", role: "", email: "", industry: "", project_type: "", tags_approx: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block space-y-1.5"><span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{label}</span>{children}</label>
);

const Pick = ({ value, onChange, options, placeholder, testid }) => (
  <Select key={value || "empty"} value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-[#080D1A] h-11"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export function DemoForm() {
  const [params] = useSearchParams();
  const urlIndustry = INDUSTRIES.some((i) => i.slug === params.get("industry")) ? params.get("industry") : "";
  const [form, setForm] = useState(() => ({ ...EMPTY, industry: urlIndustry }));
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (urlIndustry) setForm((f) => ({ ...f, industry: urlIndustry }));
  }, [urlIndustry]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.industry) return setState({ busy: false, done: false, error: "Please choose an industry." });
    setState({ busy: true, done: false, error: "" });
    try {
      await api.post("/demo-requests", form);
      setState({ busy: false, done: true, error: "" });
      setForm(EMPTY);
    } catch (err) {
      setState({ busy: false, done: false, error: errMsg(err) });
    }
  };

  return (
    <section id="demo" data-testid="section-demo" className="py-20 md:py-28 border-t border-[#1E2D4A] grid-bg">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHead eyebrow="Request a demo" title="See your project in Pulse." text="Tell us about your project. We'll show you the forecast working on data like yours." />
        </div>
        <div className="lg:col-span-8 panel p-6 md:p-8">
          {state.done ? (
            <div className="py-12 text-center" data-testid="demo-form-success">
              <CheckCircle2 className="w-10 h-10 text-amber-500 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-slate-100">Thank you. Request received.</h3>
              <p className="text-slate-400 mt-2">We'll be in touch shortly.</p>
              <button onClick={() => setState({ busy: false, done: false, error: "" })} className="mt-6 text-sm text-amber-400 hover:underline" data-testid="demo-form-again">Send another request</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="demo-form">
              <Field label="Name *"><Input required value={form.name} onChange={set("name")} className="bg-[#080D1A] h-11" data-testid="demo-name-input" /></Field>
              <Field label="Company *"><Input required value={form.company} onChange={set("company")} className="bg-[#080D1A] h-11" data-testid="demo-company-input" /></Field>
              <Field label="Role"><Input value={form.role} onChange={set("role")} placeholder="e.g. Commissioning Manager" className="bg-[#080D1A] h-11" data-testid="demo-role-input" /></Field>
              <Field label="Email *"><Input required type="email" value={form.email} onChange={set("email")} className="bg-[#080D1A] h-11" data-testid="demo-email-input" /></Field>
              <Field label="Industry *"><Pick testid="demo-industry-select" value={form.industry} onChange={set("industry")} placeholder="Choose industry" options={[...INDUSTRIES.map((i) => [i.slug, i.name]), ["other", "Other"]]} /></Field>
              <Field label="Project type"><Pick testid="demo-project-type-select" value={form.project_type} onChange={set("project_type")} placeholder="Choose project type" options={[...PROJECT_TYPES.map((p) => [p.name, p.name]), ["Other", "Other"]]} /></Field>
              <Field label="Approximate number of tags"><Pick testid="demo-tags-select" value={form.tags_approx} onChange={set("tags_approx")} placeholder="Choose range" options={TAG_BANDS.map((b) => [b, b])} /></Field>
              <div className="sm:col-span-2"><Field label="Message"><Textarea rows={4} value={form.message} onChange={set("message")} className="bg-[#080D1A]" data-testid="demo-message-input" /></Field></div>
              {state.error && <p className="sm:col-span-2 text-sm text-red-400" data-testid="demo-form-error">{state.error}</p>}
              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-slate-500">We use these details only to respond to your request. See our <a href="/privacy" className="underline">privacy policy</a>.</p>
                <button type="submit" disabled={state.busy} data-testid="demo-form-submit" className="btn-accent px-8 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                  {state.busy && <Loader2 className="w-4 h-4 animate-spin" />}Request a demo
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
